import { resolveGoalTreeLink } from '@griply/utils';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Target } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  TaskMetaInteraction,
} from 'shared/components/ui/task-meta-interaction';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { Paths } from 'shared/routes/paths';
import { Goal, NewGoalWithID } from 'shared/types/goal';
import { GoalLinkPickerSlotProps } from 'shared/types/goal-link-picker';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { LinkOptions } from 'shared/types/task';
import { getGoalDetailUrl } from 'shared/utils/get-goal-detail-url';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

import * as Styled from './task-meta-interaction-linked-item.style';

export type TaskMetaInteractionGoalProps = Omit<
  React.ComponentProps<typeof TaskMetaInteraction>,
  'start' | 'onClick' | 'label' | 'selected' | 'tooltipLabel'
> & {
  goals: (Goal | NewGoalWithID)[];
  lifeAreas: LifeArea[];
  value?: ID | null;
  LinkPicker?: React.FC<GoalLinkPickerSlotProps>;
  onChange: (link: { id: ID; type: LinkOptions } | null) => void;
  /** Stages the goal instead of letting the picker persist it — drafts create it on submit. */
  onCreateGoal?: GoalLinkPickerSlotProps['onCreateGoal'];
  showGoalColor?: boolean;
  hasIcon?: boolean;
  highlight?: boolean;
  isCreate?: boolean;
};

export const TaskMetaInteractionLinkedItem: React.FC<
  TaskMetaInteractionGoalProps
> = ({
  goals,
  lifeAreas,
  value,
  LinkPicker,
  onChange,
  onCreateGoal,
  hasIcon,
  highlight,
  isCreate,
  ...rest
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const { item, itemIsGoal: isGoalItem } = useMemo(() => {
    const goalItem = goals.find(({ id }) => id === value);
    if (goalItem) {
      return { item: goalItem, itemIsGoal: true };
    }
    const lifeAreaItem = lifeAreas.find(({ id }) => id === value);
    return { item: lifeAreaItem, itemIsGoal: false };
  }, [goals, lifeAreas, value]);

  const color = useMemo(
    () =>
      item?.id
        ? isGoalItem
          ? getLinkedItemColor(item.id, { lifeAreas, goals }) ?? undefined
          : item.color
        : undefined,
    [item?.id, item?.color, isGoalItem, lifeAreas, goals],
  );

  const currentSelection = useMemo(() => {
    if (!value) {
      return undefined;
    }
    return goals.some(({ id }) => id === value)
      ? { goalId: value }
      : { lifeAreaId: value };
  }, [goals, value]);

  const handleSelect = (ancestorIds: ID[], lifeAreaId: ID | null) =>
    _onChange(resolveGoalTreeLink(ancestorIds, lifeAreaId));

  const _onChange = (link: { id: ID; type: LinkOptions } | null) => {
    onChange(link);
    closeMenu();
  };

  const navigateToItem = () => {
    if (!item) {
      return;
    }

    navigate(
      isGoalItem
        ? getGoalDetailUrl(item)
        : replaceUrlParams(Paths.LifeAreaDetail, { id: item.id }),
    );
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, [menuOpen]);

  return (
    <Styled.Container ref={containerRef}>
      <TaskMetaInteraction
        start={
          color ? (
            <Styled.Color $color={color} />
          ) : hasIcon ? (
            <Icon icon={Target} />
          ) : undefined
        }
        color={highlight && item ? Colors.TextPrimary : Colors.Inherit}
        onClick={item && !isCreate ? navigateToItem : openMenu}
        label={item?.name}
        selected={menuOpen}
        tooltipLabel={t(
          item
            ? 'task.linked-item.tooltip.edit.label'
            : 'task.linked-item.tooltip.add.label',
        )}
        {...rest}
      />

      {menuOpen && !!LinkPicker && (
        <LinkPicker
          currentSelection={currentSelection}
          onSelect={handleSelect}
          onClose={closeMenu}
          referenceElement={containerRef}
          onCreateGoal={onCreateGoal}
        />
      )}
    </Styled.Container>
  );
};
